import {
  ClockIcon,
  CheckCircleIcon,
  ForbiddenIcon,
  AttentionTriangleIcon,
} from '@rsos/assets/icons';
import type {
  SelectOption,
  FormField,
  AirAmbulanceFormValues,
  StatusPageState,
  StatusType,
  ValidationMessages,
  HeaderConfig,
} from '../types';

export const REQUEST_REASONS: SelectOption[] = [
  { label: 'Trauma - Adult', value: 'Trauma - Adult' },
  { label: 'Trauma - Pediatric', value: 'Trauma - Pediatric' },
  { label: 'Medical - Adult', value: 'Medical - Adult' },
  { label: 'Medical - Pediatric', value: 'Medical - Pediatric' },
  { label: 'Burn - Adult', value: 'Burn - Adult' },
  { label: 'Burn - Pediatric', value: 'Burn - Pediatric' },
  { label: 'Mass Casualty', value: 'Mass Casualty' },
  { label: 'Cardiac NOS', value: 'Cardiac NOS' },
  {
    label: 'Nuerological (Non-Stroke-CVA)',
    value: 'Nuerological (Non-Stroke-CVA)',
  },
  { label: 'OBGYN', value: 'OBGYN' },
  { label: 'STEMI', value: 'STEMI' },
  { label: 'Stroke', value: 'Stroke' },
  { label: 'Surgical', value: 'Surgical' },
  { label: 'Toxicology', value: 'Toxicology' },
  { label: 'Specialty Team - Cardiac', value: 'Specialty Team - Cardiac' },
  {
    label: 'Specialty Team - High Risk OB',
    value: 'Specialty Team - High Risk OB',
  },
  { label: 'Specialty Team - Neonatal', value: 'Specialty Team - Neonatal' },
  { label: 'Specialty Team - NOS', value: 'Specialty Team - NOS' },
  { label: 'Specialty Team - Pediatric', value: 'Specialty Team - Pediatric' },
];

export const LAUNCH_TYPES = {
  IMMEDIATE: 'Immediate',
  STANDBY: 'Standby',
  TEST: 'Test',
} as const;

export const LANDING_ZONE_EXTERNAL_ID = 'landing_zone_external_id';

export const FORM_FIELDS: FormField[] = [
  {
    label: 'Multiple Units Requested?*',
    field: 'multiple_units_requested',
  },
  {
    label: 'Launch Type*',
    field: 'launch_type',
    options: [LAUNCH_TYPES.IMMEDIATE, LAUNCH_TYPES.STANDBY, LAUNCH_TYPES.TEST],
  },
  {
    label: 'Has any other air service declined due to weather?*',
    field: 'air_service_declined',
  },
  {
    label: 'Is any other air service responding to the scene?*',
    field: 'air_service_responding',
  },
];

export const DEFAULT_FORM_VALUES: AirAmbulanceFormValues = {
  pickup_coordinates: '',
  pickup_location: '',
  pickup_details: '',
  ground_contact: '',
  patient_info: '',
  request_reason: 'Select',
  multiple_units_requested: 'No',
  launch_type: 'Immediate',
  air_service_declined: '',
  air_service_responding: '',
  // Stores the selected landing zone's external_id when a landing zone is chosen
  [LANDING_ZONE_EXTERNAL_ID]: '',
};

export const DEFAULT_BUTTON_OPTIONS: string[] = ['No', 'Yes'];

export const COORDINATE_VALIDATION: ValidationMessages = {
  REQUIRED: 'Coordinates are required',
  INVALID_FORMAT: 'Invalid coordinates format. Use "latitude, longitude"',
  INVALID_LATITUDE: 'Latitude must be between -90 and 90',
  INVALID_LONGITUDE: 'Longitude must be between -180 and 180',
};

export const ADDRESS_VALIDATION: ValidationMessages = {
  REQUIRED: 'Address is required',
  NO_MATCH: 'No matching address found',
  SELECT_SUGGESTION: 'Please select a valid address from the suggestions',
};

export const REQUIRED_FIELDS: (keyof AirAmbulanceFormValues)[] = [
  'pickup_coordinates',
  'pickup_location',
  'ground_contact',
  'patient_info',
  'request_reason',
  'air_service_declined',
  'air_service_responding',
  'multiple_units_requested',
  'launch_type',
];

export const FORM_PAGES = {
  FORM: 'air_ambulance_form',
  STATUS: 'air_ambulance_status',
} as const;

export const CANCEL_REQUEST_REASONS: SelectOption[] = [
  { label: 'Cancelled By Requestor', value: 'cancelled_by_requestor' },
  { label: 'Not Medically Necessary', value: 'not_medically_necessary' },
  { label: 'Other Mode of Transport', value: 'other_mode_of_transport' },
  {
    label: 'Patient Clinically Unstable',
    value: 'patient_clinically_unstable',
  },
  { label: 'Patient Expired', value: 'patient_expired' },
  { label: 'Patient Refused Transport', value: 'patient_refused_transport' },
  { label: 'Test Call', value: 'test_call' },
  { label: 'Unacceptable ETA', value: 'unacceptable_eta' },
  { label: 'Other', value: 'other' },
];

export const FORM_HEADER: HeaderConfig = {
  text: 'Request Air Ambulance',
};

export const STATUS_HEADER: HeaderConfig = {
  text: 'Air Ambulance Status',
};

export const REQUEST_REASON = 'request_reason';
export const PICKUP_COORDINATES = 'pickup_coordinates';
export const PICKUP_LOCATION = 'pickup_location';
export const PICKUP_DETAILS = 'pickup_details';
export const GROUND_CONTACT = 'ground_contact';
export const PATIENT_INFO = 'patient_info';

export const STATUS_PAGE_STATES: Record<string, StatusPageState> = {
  SUBMITTED: {
    value: 'submitted',
    message: 'Air ambulance request sent, awaiting response',
    bgColor: 'primaryBase',
    icon: ClockIcon,
  },
  CANCELLED: {
    value: 'cancelled',
    message: 'Air ambulance request cancelled',
    bgColor: 'heavyDanger',
    icon: ForbiddenIcon,
  },
  ACCEPTED: {
    value: 'accepted',
    message: 'Air ambulance request successful',
    bgColor: 'success',
    icon: CheckCircleIcon,
  },
  TEST: {
    value: 'test',
    message:
      'AIR AMBULANCE SYSTEM TEST. THIS IS NOT AN EMERGENCY. DO NOT DISPATCH.',
    bgColor: 'caution',
    icon: AttentionTriangleIcon,
  },
};

export const STATUS_TYPES: Record<string, StatusType> = {
  accepted: {
    value: 'accepted',
    pageState: STATUS_PAGE_STATES.ACCEPTED,
  },
  dispatchReceived: {
    value: 'dispatchReceived',
    pageState: STATUS_PAGE_STATES.ACCEPTED,
  },
  dispatchReceivedStandby: {
    value: 'dispatchReceivedStandby',
    pageState: STATUS_PAGE_STATES.ACCEPTED,
  },
  dispatchReviewing: {
    value: 'dispatchReviewing',
    pageState: STATUS_PAGE_STATES.ACCEPTED,
  },
  checkingWeather: {
    value: 'checkingWeather',
    pageState: STATUS_PAGE_STATES.ACCEPTED,
  },
  preparingToLaunch: {
    value: 'preparingToLaunch',
    pageState: STATUS_PAGE_STATES.ACCEPTED,
  },
  enRouteToScene: {
    value: 'enRouteToScene',
    pageState: STATUS_PAGE_STATES.ACCEPTED,
  },
  arrivedToScene: {
    value: 'arrivedToScene',
    pageState: STATUS_PAGE_STATES.ACCEPTED,
  },
  transporting: {
    value: 'transporting',
    pageState: STATUS_PAGE_STATES.ACCEPTED,
  },
  arrivedToDropoff: {
    value: 'arrivedToDropoff',
    pageState: STATUS_PAGE_STATES.ACCEPTED,
  },
  complete: {
    value: 'complete',
    pageState: STATUS_PAGE_STATES.ACCEPTED,
  },
  pendingCancellation: {
    value: 'pendingCancellation',
    pageState: STATUS_PAGE_STATES.CANCELLED,
  },
  cancelled: {
    value: 'cancelled',
    pageState: STATUS_PAGE_STATES.CANCELLED,
  },
  dispatchDelayed: {
    value: 'dispatchDelayed',
    pageState: STATUS_PAGE_STATES.ACCEPTED,
  },
  error: {
    value: 'error',
    pageState: STATUS_PAGE_STATES.CANCELLED,
  },
  cancelPending: {
    value: 'cancelPending',
    pageState: STATUS_PAGE_STATES.CANCELLED,
  },
  cancelDisregarded: {
    value: 'cancelDisregarded',
    pageState: STATUS_PAGE_STATES.ACCEPTED,
  },
  cancelCompleted: {
    value: 'cancelCompleted',
    pageState: STATUS_PAGE_STATES.CANCELLED,
  },
  editLocationPending: {
    value: 'editLocationPending',
    pageState: STATUS_PAGE_STATES.ACCEPTED,
  },
  editLocationDisregarded: {
    value: 'editLocationDisregarded',
    pageState: STATUS_PAGE_STATES.ACCEPTED,
  },
  editLocationCompleted: {
    value: 'editLocationCompleted',
    pageState: STATUS_PAGE_STATES.ACCEPTED,
  },
  activationRequested: {
    value: 'activationRequested',
    pageState: STATUS_PAGE_STATES.ACCEPTED,
  }, // PSX-9103
  checkingWeatherStandby: {
    value: 'checkingWeatherStandby',
    pageState: STATUS_PAGE_STATES.ACCEPTED,
  }, // PSX-9103
  dispatchReviewingStandby: {
    value: 'dispatchReviewingStandby',
    pageState: STATUS_PAGE_STATES.ACCEPTED,
  }, // PSX-9103
  waitingForActivation: {
    value: 'waitingForActivation',
    pageState: STATUS_PAGE_STATES.ACCEPTED,
  }, // PSX-9103
  activationPending: {
    value: 'activationPending',
    pageState: STATUS_PAGE_STATES.ACCEPTED,
  }, // PSX-9103
  activationCompleted: {
    value: 'activationCompleted',
    pageState: STATUS_PAGE_STATES.ACCEPTED,
  }, // PSX-9103
};
